import type { DataRecord, GoogleLocal } from "../../../types";

/**
 * Calculos del Resumen del sandbox. Todo sale de los campos reales de los
 * comentarios (platform, origin, polaridad, aspecto, star_rating, has_reply,
 * post_id, post_title). No se inventa ningun dato.
 */

export type Chan = "gbp" | "meta_ig" | "meta_fb" | "youtube";

export const CHAN_ORDER: Chan[] = ["gbp", "meta_ig", "meta_fb", "youtube"];

export const CHAN_LABEL: Record<Chan, string> = {
  gbp: "Google Business Profile",
  meta_ig: "Instagram",
  meta_fb: "Facebook",
  youtube: "YouTube",
};

export const CHAN_SHORT: Record<Chan, string> = {
  gbp: "Google",
  meta_ig: "Instagram",
  meta_fb: "Facebook",
  youtube: "YouTube",
};

export const CHAN_KIND: Record<Chan, string> = {
  gbp: "Reseñas",
  meta_ig: "Comentarios",
  meta_fb: "Comentarios",
  youtube: "Comentarios",
};

export const str = (v: unknown, fb = ""): string => (v === null || v === undefined ? fb : String(v));

export function num(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function truthy(v: unknown): boolean {
  return v === true || v === "true" || v === 1 || v === "1";
}

export function pct(n: number, total: number): number {
  return total > 0 ? Math.round((n / total) * 100) : 0;
}

export function fmtDecimal(n: number, digits = 1): string {
  return n.toFixed(digits).replace(".", ",");
}

export function fmtInt(n: number): string {
  return n.toLocaleString("es-CL");
}

export function channelOf(c: DataRecord): Chan | null {
  const p = str(c.platform).toLowerCase();
  if (p === "gbp" || p.includes("google")) return "gbp";
  if (p === "meta_ig" || p.includes("instagram")) return "meta_ig";
  if (p === "meta_fb" || p.includes("facebook")) return "meta_fb";
  if (p === "youtube") return "youtube";
  return null;
}

export type Origen = "all" | "organic" | "paid";

/** Anuncio: `origin == 'paid'`. Cualquier otro valor es orgánico. */
export const isPaid = (c: DataRecord): boolean => str(c.origin).toLowerCase() === "paid";

export function byOrigin(rows: DataRecord[], origen: Origen): DataRecord[] {
  return origen === "all" ? rows : rows.filter((c) => isPaid(c) === (origen === "paid"));
}

export interface OrigenCounts {
  all: number;
  organic: number;
  paid: number;
}

export function countByOrigin(rows: DataRecord[]): OrigenCounts {
  const paid = rows.filter(isPaid).length;
  return { all: rows.length, organic: rows.length - paid, paid };
}

export type Tone = "pos" | "neu" | "neg" | "none";

export function toneOf(c: DataRecord): Tone {
  const p = str(c.polaridad).toLowerCase();
  if (p === "positiva") return "pos";
  if (p === "negativa") return "neg";
  if (p === "neutra") return "neu";
  return "none";
}

export interface ToneCount {
  pos: number;
  neu: number;
  neg: number;
  none: number;
  total: number;
}

export function countTone(rows: DataRecord[]): ToneCount {
  const t: ToneCount = { pos: 0, neu: 0, neg: 0, none: 0, total: rows.length };
  for (const c of rows) t[toneOf(c)] += 1;
  return t;
}

const ASPECT_LABELS: Record<string, string> = {
  atencion: "Atención",
  servicio: "Servicio",
  producto: "Producto",
  precio: "Precio",
  trato_y_atencion: "Trato y atención",
  resultados_y_tramites: "Resultados y trámites",
  profesionales_y_resultado: "Profesionales y resultado",
  tiempos_de_espera: "Tiempos de espera",
};

export function aspectLabel(raw: string): string {
  const key = raw.trim().toLowerCase();
  if (ASPECT_LABELS[key]) return ASPECT_LABELS[key];
  const spaced = key.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Aspecto mas frecuente, ignorando vacios y "otro". */
export function topAspect(rows: DataRecord[]): { label: string; count: number } | null {
  const counts: Record<string, number> = {};
  for (const c of rows) {
    const a = str(c.aspecto).trim().toLowerCase();
    if (!a || a === "otro" || a === "null") continue;
    counts[a] = (counts[a] ?? 0) + 1;
  }
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;
  return { label: aspectLabel(entries[0][0]), count: entries[0][1] };
}

const ACT_LABELS: Record<string, string> = {
  pregunta: "Preguntas",
  elogio: "Elogios",
  critica: "Críticas",
  reclamo: "Reclamos",
  solicitud_contacto: "Solicitudes de contacto",
  objecion_precio: "Objeciones de precio",
};

/** Acto de habla dominante sin contar reacciones ni spam. */
export function topAct(rows: DataRecord[]): { label: string; count: number } | null {
  const counts: Record<string, number> = {};
  for (const c of rows) {
    const a = str(c.acto_de_habla).trim().toLowerCase();
    if (!a || !ACT_LABELS[a]) continue;
    counts[a] = (counts[a] ?? 0) + 1;
  }
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;
  return { label: ACT_LABELS[entries[0][0]], count: entries[0][1] };
}

/* Canales */

export interface ChannelStats {
  chan: Chan;
  total: number;
  tone: ToneCount;
  paid: number;
  negPending: number;
  rated: number;
  avg: number | null;
  topTopic: string | null;
}

export function channelStats(rows: DataRecord[]): ChannelStats[] {
  const out: ChannelStats[] = [];
  for (const chan of CHAN_ORDER) {
    const sub = rows.filter((c) => channelOf(c) === chan);
    if (sub.length === 0) continue;
    const ratings = sub.map((c) => num(c.star_rating)).filter((r): r is number => r !== null);
    const avg = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : null;
    out.push({
      chan,
      total: sub.length,
      tone: countTone(sub),
      paid: sub.filter((c) => str(c.origin).toLowerCase() === "paid").length,
      negPending: sub.filter((c) => toneOf(c) === "neg" && !truthy(c.has_reply)).length,
      rated: ratings.length,
      avg,
      topTopic: topAspect(sub)?.label ?? null,
    });
  }
  return out;
}

/* Google: serie mensual y estrellas */

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const MONTHS_FULL = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

export interface MonthPoint {
  key: string;
  label: string;
  monthName: string;
  year: number;
  count: number;
  avg: number | null;
  /** Reseñas de 1 y 2 estrellas del mes. */
  low: number;
  /** Reseñas de 4 y 5 estrellas del mes. */
  high: number;
  current: boolean;
}

/** Nota objetivo de un local o de la marca: el gráfico mensual pinta en verde la zona desde aquí hasta 5,0. */
export const TARGET_RATING = 4.5;

export interface MonthlyTrend {
  /** Cambio de la nota por mes. */
  slope: number;
  /** Nota que marca la recta en el mes de posición `i`. */
  at: (i: number) => number;
  /** Posición del primer y del último mes cerrado con reseñas. */
  from: number;
  to: number;
}

/** Recta de mínimos cuadrados sobre los meses cerrados con reseñas, ponderada por reseñas del mes. Sin tendencia con menos de 3 meses. */
export function monthlyTrend(pts: MonthPoint[]): MonthlyTrend | null {
  const closed = pts.map((p, i) => ({ i, y: p.avg, w: p.count })).filter((q): q is { i: number; y: number; w: number } => q.y !== null && q.w > 0 && !pts[q.i].current);
  if (closed.length < 3) return null;
  const sw = closed.reduce((a, q) => a + q.w, 0);
  const xm = closed.reduce((a, q) => a + q.w * q.i, 0) / sw;
  const ym = closed.reduce((a, q) => a + q.w * q.y, 0) / sw;
  const sxx = closed.reduce((a, q) => a + q.w * (q.i - xm) ** 2, 0);
  if (sxx === 0) return null;
  const slope = closed.reduce((a, q) => a + q.w * (q.i - xm) * (q.y - ym), 0) / sxx;
  return { slope, at: (i) => ym + slope * (i - xm), from: closed[0].i, to: closed[closed.length - 1].i };
}

const MIN_MONTHS = 12;

const monthIdx = (iso: string) => Number(iso.slice(0, 4)) * 12 + Number(iso.slice(5, 7)) - 1;

/**
 * Serie mensual de la nota en Google. Cubre el rango [from, to] (null = sin límite) acotado
 * al primer mes con reseñas y al mes actual, con un mínimo de 12 meses terminando en `to`.
 * Sin `from` (undefined) devuelve los últimos 12 meses.
 */
export function monthlyGoogle(
  all: DataRecord[],
  from?: string | null,
  to?: string | null,
  now: Date = new Date(),
): MonthPoint[] {
  const base: {
    key: string;
    label: string;
    monthName: string;
    year: number;
    count: number;
    sum: number;
    low: number;
    high: number;
    current: boolean;
  }[] = [];
  const nowIdx = now.getFullYear() * 12 + now.getMonth();
  const endIdx = to ? Math.min(monthIdx(to), nowIdx) : nowIdx;
  let firstIdx = endIdx;
  for (const c of all) {
    if (channelOf(c) === "gbp" && num(c.star_rating) !== null) firstIdx = Math.min(firstIdx, monthIdx(str(c.date)));
  }
  const fromIdx = from === undefined ? endIdx - (MIN_MONTHS - 1) : from ? monthIdx(from) : firstIdx;
  const startIdx = Math.min(Math.max(fromIdx, firstIdx), endIdx - (MIN_MONTHS - 1));
  for (let idx = startIdx; idx <= endIdx; idx++) {
    const year = Math.floor(idx / 12);
    const month = idx % 12;
    base.push({
      key: `${year}-${String(month + 1).padStart(2, "0")}`,
      label: MONTHS[month],
      monthName: MONTHS_FULL[month],
      year,
      count: 0,
      sum: 0,
      low: 0,
      high: 0,
      current: idx === nowIdx,
    });
  }
  const byKey = new Map(base.map((m) => [m.key, m]));
  for (const c of all) {
    if (channelOf(c) !== "gbp" || truthy(c.is_owner_author)) continue;
    const r = num(c.star_rating);
    if (r === null) continue;
    const m = byKey.get(str(c.date).slice(0, 7));
    if (!m) continue;
    m.count += 1;
    m.sum += r;
    if (r <= 2) m.low += 1;
    if (r >= 4) m.high += 1;
  }
  return base.map((m) => ({
    key: m.key,
    label: m.label,
    monthName: m.monthName,
    year: m.year,
    count: m.count,
    avg: m.count > 0 ? Math.round((m.sum / m.count) * 10) / 10 : null,
    low: m.low,
    high: m.high,
    current: m.current,
  }));
}

export interface StarStats {
  counts: Record<number, number>;
  total: number;
  sum: number;
  avg: number | null;
}

export function starStats(rows: DataRecord[]): StarStats {
  const counts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  let total = 0;
  let sum = 0;
  for (const c of rows) {
    if (channelOf(c) !== "gbp") continue;
    const r = num(c.star_rating);
    if (r === null) continue;
    const s = Math.min(5, Math.max(1, Math.round(r)));
    counts[s] = (counts[s] ?? 0) + 1;
    total += 1;
    sum += s;
  }
  return { counts, total, sum, avg: total > 0 ? sum / total : null };
}

/** Nota resultante si hasta 10 reseñas de 1 estrella hubieran sido de 4. */
export function whatIfFourStars(stats: StarStats): { moved: number; avg: number } | null {
  const ones = stats.counts[1] ?? 0;
  if (ones === 0 || stats.total === 0) return null;
  const moved = Math.min(10, ones);
  return { moved, avg: (stats.sum + 3 * moved) / stats.total };
}

/* Locales de Google (una ficha por ubicacion) */

export interface LocationRow {
  name: string;
  reviews: number;
  avg: number | null;
  low: number;
  lowPct: number;
  complaint: string | null;
}

/** `registry` son todas las fichas del cliente: las que no tienen reseñas en `rows` salen igual, con cero. */
export function locationRows(rows: DataRecord[], registry: GoogleLocal[] = []): LocationRow[] {
  const groups = new Map<string, DataRecord[]>();
  for (const c of rows) {
    if (channelOf(c) !== "gbp") continue;
    const name = str(c.post_title).trim() || str(c.page_id).trim();
    if (!name) continue;
    const list = groups.get(name);
    if (list) list.push(c);
    else groups.set(name, [c]);
  }
  const out: LocationRow[] = [];
  groups.forEach((list, name) => {
    const rated = list.filter((c) => num(c.star_rating) !== null);
    if (rated.length === 0) return;
    const sum = rated.reduce((a, c) => a + (num(c.star_rating) ?? 0), 0);
    const lowRows = rated.filter((c) => (num(c.star_rating) ?? 5) <= 2);
    out.push({
      name,
      reviews: rated.length,
      avg: sum / rated.length,
      low: lowRows.length,
      lowPct: pct(lowRows.length, rated.length),
      complaint: topAspect(lowRows)?.label ?? null,
    });
  });
  for (const l of registry) {
    if (out.some((r) => r.name === l.name || r.name === l.id)) continue;
    out.push({ name: l.name, reviews: 0, avg: null, low: 0, lowPct: 0, complaint: null });
  }
  return out.sort((a, b) => b.reviews - a.reviews || a.name.localeCompare(b.name, "es"));
}

/* Locales de Google: una ficha por local, con su historial, el período y el anterior */

export interface BandejaFocus {
  /** Nombre del local tal como lo muestra la Bandeja. */
  location?: string;
  /** Clave del tema (aspecto en minúsculas). */
  topic?: string;
  /** Reseña que la Bandeja deja seleccionada al abrir. */
  feedbackId?: string;
  /** Canal con que abre la Bandeja. Sin valor abre en Google My Business. */
  group?: 'google' | 'meta';
  /** Muestra todos los pendientes del canal, no solo los críticos. */
  todos?: boolean;
}

/** Crítico: lo marca el servidor con la política de respuesta, igual para el Resumen, la Bandeja y los Locales. */
export function isCriticalReview(c: DataRecord): boolean {
  return truthy(c.critico);
}

/** Llave estable de la ficha: el id de la ubicación y, si falta, el nombre. */
export function localKey(c: DataRecord): string {
  return str(c.page_id).trim() || str(c.post_title).trim();
}

/** Reseñas de Google con estrellas, sin respuestas propias. */
export function ratedGoogle(rows: DataRecord[]): DataRecord[] {
  return rows.filter(
    (c) => channelOf(c) === 'gbp' && !truthy(c.is_owner_author) && !truthy(c.is_reply) && num(c.star_rating) !== null,
  );
}

export type LocalStatus = 'attention' | 'review' | 'improving' | 'holds' | 'stable' | 'none';

export interface LocalRow {
  id: string;
  name: string;
  mapsUrl: string | null;
  /** Estrellas de todo el historial de la ficha. */
  stars: StarStats;
  avg: number | null;
  /** Nota por mes de los últimos 12 meses. */
  months: MonthPoint[];
  /** Meses seguidos, hasta el último con reseñas, con nota mensual bajo 4,0. */
  monthsBelow: number;
  curN: number;
  curSum: number;
  curAvg: number | null;
  curLow: number;
  curPos: number;
  prevN: number | null;
  prevSum: number | null;
  prevAvg: number | null;
  /** Críticos sin responder en el período. */
  critOpen: number;
  status: LocalStatus;
  label: string;
  note: string;
  /** Menos de 5 reseñas en el período: la nota no es concluyente. */
  small: boolean;
}

function monthsBelowFour(pts: MonthPoint[]): number {
  let i = pts.length - 1;
  if (i >= 0 && pts[i].avg === null) i -= 1;
  let n = 0;
  while (i >= 0 && pts[i].avg !== null && (pts[i].avg as number) < 4) {
    n += 1;
    i -= 1;
  }
  return n;
}

const LOCAL_ORDER: Record<LocalStatus, number> = { attention: 0, review: 1, improving: 2, holds: 2, stable: 2, none: 3 };

/** Un local por ficha de Google. `all` es el historial, `cur` el período activo y `prev` el anterior de igual largo. */
export function localesData(all: DataRecord[], cur: DataRecord[], prev: DataRecord[] | null, registry: GoogleLocal[] = []): LocalRow[] {
  const allG = ratedGoogle(all);
  const curG = ratedGoogle(cur);
  const prevG = prev ? ratedGoogle(prev) : null;
  const groups = new Map<string, DataRecord[]>();
  for (const c of allG) {
    const k = localKey(c);
    if (!k) continue;
    const list = groups.get(k);
    if (list) list.push(c);
    else groups.set(k, [c]);
  }
  const registered = new Map(registry.map((l) => [l.id, l]));
  registered.forEach((_, id) => {
    if (!groups.has(id)) groups.set(id, []);
  });
  const sumOf = (rows: DataRecord[]): number => rows.reduce((a, c) => a + (num(c.star_rating) ?? 0), 0);
  const out: LocalRow[] = [];
  groups.forEach((rows, id) => {
    const latest = rows.length ? rows.reduce((a, b) => (str(b.date) > str(a.date) ? b : a)) : null;
    const name = registered.get(id)?.name || str(latest?.post_title).trim() || id;
    const mapsUrl =
      rows
        .map((c) => str(c.post_permalink).trim())
        .find((u) => u.startsWith('https://maps.google.com/') || u.startsWith('https://www.google.com/maps')) ??
      registered.get(id)?.mapsUrl ??
      null;
    const curRows = curG.filter((c) => localKey(c) === id);
    const prevRows = prevG ? prevG.filter((c) => localKey(c) === id) : null;
    const stars = starStats(rows);
    const months = monthlyGoogle(rows);
    const below = monthsBelowFour(months);
    const curSum = sumOf(curRows);
    const prevSum = prevRows ? sumOf(prevRows) : null;
    const curAvg = curRows.length ? curSum / curRows.length : null;
    const prevAvg = prevRows && prevRows.length ? (prevSum as number) / prevRows.length : null;
    const lowRows = curRows.filter((c) => (num(c.star_rating) ?? 5) <= 2);
    const posN = curRows.filter((c) => (num(c.star_rating) ?? 0) >= 4).length;
    const critOpen = curRows.filter(isCriticalReview).length;
    const small = curRows.length < 5;

    let status: LocalStatus;
    let label: string;
    if (curRows.length === 0) {
      status = 'none';
      label = 'Sin reseñas nuevas';
    } else if (small) {
      if (lowRows.length > 0) {
        status = 'review';
        label = 'Revisar';
      } else {
        status = 'stable';
        label = 'Estable';
      }
    } else {
      const a = curAvg as number;
      if (a < 4) {
        status = 'attention';
        label = 'Requiere atención';
      } else if (a >= 4.3 && prevAvg !== null && a - prevAvg >= 0.3) {
        status = 'improving';
        label = 'Mejora';
      } else if (a >= 4.3) {
        status = 'holds';
        label = 'Sostiene la nota';
      } else {
        status = 'stable';
        label = 'Estable';
      }
    }

    const avgText = stars.avg !== null ? fmtDecimal(stars.avg) : '-';
    const complaint = topAspect(lowRows);
    const plRes = curRows.length === 1 ? 'reseña' : 'reseñas';
    let note: string;
    if (status === 'none') {
      note =
        stars.total === 0
          ? 'Esta ficha aún no tiene reseñas.'
          : `Sin reseñas en el período. Nota acumulada ${avgText} con ${fmtInt(stars.total)} reseñas.`;
    } else if (status === 'attention') {
      const bits: string[] = [];
      if (below >= 2) bits.push(`${below} meses seguidos con nota mensual bajo 4,0.`);
      if (complaint) bits.push(`La queja principal del período es ${complaint.label} (${complaint.count} de ${lowRows.length}).`);
      if (bits.length === 0) bits.push(`Nota ${fmtDecimal(curAvg as number)} en el período contra ${avgText} acumulada.`);
      note = bits.join(' ');
    } else if (status === 'review') {
      note =
        `${lowRows.length} de ${curRows.length} ${plRes} del período ${lowRows.length === 1 ? 'es' : 'son'} de 1 a 2★` +
        (critOpen > 0 ? ` y ${critOpen} ${critOpen === 1 ? 'sigue' : 'siguen'} sin responder.` : '.') +
        ' Con tan pocas reseñas la nota acumulada se mueve poco.';
    } else {
      note =
        `${curRows.length} ${plRes} del período, ${lowRows.length === 0 ? 'ninguna de 1 a 2★' : `${lowRows.length} de 1 a 2★`}.` +
        (small ? ' Muestra pequeña.' : '') +
        ` Nota acumulada ${avgText}.`;
    }

    out.push({
      id,
      name,
      mapsUrl,
      stars,
      avg: stars.avg,
      months,
      monthsBelow: below,
      curN: curRows.length,
      curSum,
      curAvg,
      curLow: lowRows.length,
      curPos: posN,
      prevN: prevRows ? prevRows.length : null,
      prevSum,
      prevAvg,
      critOpen,
      status,
      label,
      note,
      small,
    });
  });
  return out.sort((a, b) => LOCAL_ORDER[a.status] - LOCAL_ORDER[b.status] || b.stars.total - a.stars.total);
}

export interface LocalesSummary {
  total: number;
  avg: number | null;
  curN: number;
  curAvg: number | null;
  prevN: number | null;
  prevAvg: number | null;
  critOpen: number;
  critLocals: number;
}

export function localesSummary(rows: LocalRow[]): LocalesSummary {
  const total = rows.reduce((a, r) => a + r.stars.total, 0);
  const sum = rows.reduce((a, r) => a + r.stars.sum, 0);
  const curN = rows.reduce((a, r) => a + r.curN, 0);
  const curSum = rows.reduce((a, r) => a + r.curSum, 0);
  const hasPrev = rows.some((r) => r.prevN !== null);
  const prevN = hasPrev ? rows.reduce((a, r) => a + (r.prevN ?? 0), 0) : null;
  const prevSum = hasPrev ? rows.reduce((a, r) => a + (r.prevSum ?? 0), 0) : null;
  return {
    total,
    avg: total > 0 ? sum / total : null,
    curN,
    curAvg: curN > 0 ? curSum / curN : null,
    prevN,
    prevAvg: prevN && prevSum !== null ? prevSum / prevN : null,
    critOpen: rows.reduce((a, r) => a + r.critOpen, 0),
    critLocals: rows.filter((r) => r.critOpen > 0).length,
  };
}

/** Frase de Gertrudis para la vista de Locales: dónde se concentran las reseñas de 1 a 2 estrellas. */
export function localesInsight(rows: LocalRow[], critOpen: number): InsightSeg[] | null {
  const withLow = rows.filter((r) => r.curLow > 0).sort((a, b) => b.curLow - a.curLow);
  const lowTotal = withLow.reduce((a, r) => a + r.curLow, 0);
  if (lowTotal === 0) return null;
  const segs: InsightSeg[] = [
    { text: `De las ${lowTotal} ${lowTotal === 1 ? 'reseña' : 'reseñas'} de 1 a 2★ del período, ` },
  ];
  withLow.forEach((r, i) => {
    if (i > 0) segs.push({ text: i === withLow.length - 1 ? ' y ' : ', ' });
    segs.push({ text: `${r.curLow} ${r.curLow === 1 ? 'es' : 'son'} de ` }, { text: r.name, bold: true });
  });
  segs.push({
    text: critOpen > 0 ? `. Hay ${critOpen} ${critOpen === 1 ? 'crítico' : 'críticos'} sin responder.` : '.',
  });
  return segs;
}

export interface LocalInsight {
  id: 'alert' | 'pattern' | 'opportunity';
  label: string;
  tone: 'alert' | 'opportunity';
  segs: InsightSeg[];
}

/** Lecturas de un local: alerta, patrón de quejas y oportunidad. `topics` son sus temas de los últimos 6 meses. */
export function localInsights(row: LocalRow, topics: TopicSummary): LocalInsight[] {
  const out: LocalInsight[] = [];

  const alert: InsightSeg[] = [];
  if (row.monthsBelow >= 2) {
    alert.push({ text: `${row.monthsBelow} meses seguidos`, bold: true }, { text: ' con nota mensual bajo 4,0. ' });
  }
  if (row.critOpen > 0) {
    alert.push(
      { text: `${row.critOpen} ${row.critOpen === 1 ? 'reseña crítica' : 'reseñas críticas'}`, bold: true },
      { text: ' sin responder en el período.' },
    );
  }
  if (alert.length > 0) out.push({ id: 'alert', label: 'Alerta', tone: 'alert', segs: alert });

  const negTotal = topics.rows.reduce((a, t) => a + t.tone.neg, 0);
  const worst = [...topics.rows].sort((a, b) => b.tone.neg - a.tone.neg)[0];
  if (worst && negTotal >= 3 && worst.tone.neg >= 2) {
    out.push({
      id: 'pattern',
      label: 'Patrón',
      tone: 'alert',
      segs: [
        { text: 'En los últimos 6 meses, ' },
        { text: worst.name, bold: true },
        { text: ` concentra ${worst.tone.neg} de las ${negTotal} reseñas negativas con tema asignado.` },
      ],
    });
  }

  const best = topics.rows.filter((t) => t.total >= 5 && t.tone.neg === 0 && t.tone.pos > 0).sort((a, b) => b.tone.pos - a.tone.pos)[0];
  if (best) {
    out.push({
      id: 'opportunity',
      label: 'Oportunidad',
      tone: 'opportunity',
      segs: [
        { text: best.name, bold: true },
        { text: ` tiene ${best.tone.pos} ${best.tone.pos === 1 ? 'reseña positiva' : 'reseñas positivas'} y ninguna negativa en los últimos 6 meses.` },
      ],
    });
  }
  return out;
}

/* Publicaciones y anuncios de Instagram y Facebook */

export interface PostRow {
  id: string;
  name: string;
  chan: Chan;
  paid: boolean;
  total: number;
  tone: ToneCount;
  repeated: string;
  pending: number;
  /** Comentarios de la publicación: sin responder primero, luego el más reciente. */
  comments: DataRecord[];
}

const commentStamp = (c: DataRecord): string => str(c.created_time) || str(c.date);

export function postRows(rows: DataRecord[], limit = 8): PostRow[] {
  const groups = new Map<string, DataRecord[]>();
  for (const c of rows) {
    const chan = channelOf(c);
    if (chan !== "meta_ig" && chan !== "meta_fb") continue;
    const id = str(c.post_id).trim() || str(c.post_title).trim();
    if (!id) continue;
    const key = `${chan}|${id}`;
    const list = groups.get(key);
    if (list) list.push(c);
    else groups.set(key, [c]);
  }
  const out: PostRow[] = [];
  groups.forEach((list, key) => {
    const first = list[0];
    const chan = channelOf(first) as Chan;
    const title = str(first.post_title).trim();
    const name = title ? (title.length > 90 ? `${title.slice(0, 87)}...` : title) : "Publicación sin título";
    const act = topAct(list);
    const asp = topAspect(list);
    out.push({
      id: key,
      name,
      chan,
      paid: str(first.origin).toLowerCase() === "paid",
      total: list.length,
      tone: countTone(list),
      repeated: act ? `${act.label} (${act.count})` : asp ? `${asp.label} (${asp.count})` : "Solo reacciones",
      pending: list.filter((c) => toneOf(c) === "neg" && !truthy(c.has_reply)).length,
      comments: list
        .slice()
        .sort(
          (a, b) =>
            Number(truthy(a.has_reply)) - Number(truthy(b.has_reply)) || commentStamp(b).localeCompare(commentStamp(a)),
        ),
    });
  });
  return out.sort((a, b) => b.total - a.total).slice(0, limit);
}

/* Temas: aspectos que clasificó Gertrudis, con el cambio en quejas */

export interface TopicRow {
  key: string;
  name: string;
  total: number;
  tone: ToneCount;
  /** % positivos menos % negativos sobre los comentarios clasificados. */
  net: number | null;
  /** Negativos del tema en el período actual. */
  negNow: number;
  /** Negativos del mismo tema en el período anterior. null si no hay período anterior. */
  negPrev: number | null;
  /** Positivos del mismo tema en el período anterior. null si no hay período anterior. */
  posPrev?: number | null;
}

export interface TopicSummary {
  rows: TopicRow[];
  /** Comentarios del período con un tema asignado. */
  assigned: number;
  /** Comentarios del período en total. */
  total: number;
}

function topicKey(c: DataRecord): string | null {
  const a = str(c.aspecto).trim().toLowerCase();
  if (!a || a === "otro" || a === "null") return null;
  return a;
}

/** Agrupa por tema los comentarios del período y los compara con los negativos del período anterior. */
export function topicRows(cur: DataRecord[], prev: DataRecord[] | null, limit = 8): TopicSummary {
  const groups = new Map<string, DataRecord[]>();
  let assigned = 0;
  for (const c of cur) {
    const k = topicKey(c);
    if (!k) continue;
    assigned += 1;
    const list = groups.get(k);
    if (list) list.push(c);
    else groups.set(k, [c]);
  }
  const prevNeg = new Map<string, number>();
  const prevPos = new Map<string, number>();
  if (prev) {
    for (const c of prev) {
      const k = topicKey(c);
      if (k && toneOf(c) === "neg") prevNeg.set(k, (prevNeg.get(k) ?? 0) + 1);
      if (k && toneOf(c) === "pos") prevPos.set(k, (prevPos.get(k) ?? 0) + 1);
    }
  }
  const rows: TopicRow[] = [];
  groups.forEach((list, key) => {
    const tone = countTone(list);
    const classified = tone.pos + tone.neu + tone.neg;
    rows.push({
      key,
      name: aspectLabel(key),
      total: list.length,
      tone,
      net: classified > 0 ? Math.round(((tone.pos - tone.neg) / classified) * 100) : null,
      negNow: tone.neg,
      negPrev: prev ? (prevNeg.get(key) ?? 0) : null,
      posPrev: prev ? (prevPos.get(key) ?? 0) : null,
    });
  });
  rows.sort((a, b) => b.total - a.total);
  return { rows: rows.slice(0, limit), assigned, total: cur.length };
}

/* Texto de reseñas y fechas */

/** Google agrega la traducción al texto de la reseña. Se deja solo el texto original. */
export function cleanReviewText(raw: unknown): string {
  let t = str(raw).trim();
  const original = t.match(/\(Original\)\s*([\s\S]*)$/i);
  if (original) t = original[1];
  else t = t.replace(/\s*\(Translated by Google\)[\s\S]*$/i, "");
  return t.replace(/\s+/g, " ").trim();
}

export function fmtDay(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  const month = MONTHS[Number(m) - 1];
  if (!y || !month || !d) return iso;
  return `${Number(d)} ${month} ${y}`;
}

/* Tarjetas de Gertrudis: reglas simples sobre los datos del período, sin texto inventado */

export interface InsightSeg {
  text: string;
  bold?: boolean;
}

export interface Insight {
  id: "alert" | "ads" | "opportunity";
  label: string;
  tone: "alert" | "ok" | "opportunity";
  segs: InsightSeg[];
  cta: string;
  /** Id de la sección a la que lleva el enlace. */
  target: string;
}

const isQuery = (c: DataRecord): boolean => {
  const a = str(c.acto_de_habla).trim().toLowerCase();
  return a === "pregunta" || a === "solicitud_contacto";
};

const isOpen = (c: DataRecord): boolean => !truthy(c.has_reply);

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

function shortTitle(s: string, max = 60): string {
  const t = s.trim();
  return t.length > max ? `${t.slice(0, max - 3).trimEnd()}...` : t;
}

export function gertrudisInsights(input: {
  comments: DataRecord[];
  topics: TopicSummary;
  /** Críticos sin responder, tal como los muestra el indicador principal. */
  urgent: number;
}): Insight[] {
  const { comments, topics, urgent } = input;
  const out: Insight[] = [];

  // 1. Alerta: el tema cuyas quejas más subieron; si no hay, lo que está sin responder
  const worse = topics.rows
    .filter((t) => t.negPrev !== null && t.negNow >= 3 && t.negNow - (t.negPrev ?? 0) >= 2)
    .sort((a, b) => b.negNow - (b.negPrev ?? 0) - (a.negNow - (a.negPrev ?? 0)))[0];
  const pending = comments.filter((c) => toneOf(c) === "neg" && isOpen(c)).length;
  if (worse) {
    out.push({
      id: "alert",
      label: "Alerta",
      tone: "alert",
      segs: [
        { text: "Las quejas por " },
        { text: worse.name, bold: true },
        { text: ` subieron de ${worse.negPrev ?? 0} a ${worse.negNow} frente al período anterior.` },
      ],
      cta: "Ver los temas",
      target: "temas",
    });
  } else if (urgent > 0) {
    out.push({
      id: "alert",
      label: "Alerta",
      tone: "alert",
      segs: [
        { text: `${urgent} ${plural(urgent, "comentario crítico", "comentarios críticos")}`, bold: true },
        { text: ` sin responder. Son lo primero que conviene atender.` },
      ],
      cta: "Ver los canales",
      target: "canales",
    });
  } else if (pending > 0) {
    out.push({
      id: "alert",
      label: "Alerta",
      tone: "alert",
      segs: [
        { text: `${pending} ${plural(pending, "comentario negativo", "comentarios negativos")}`, bold: true },
        { text: ` sin responder.` },
      ],
      cta: "Ver los canales",
      target: "canales",
    });
  } else {
    out.push({
      id: "alert",
      label: "Sin alertas",
      tone: "ok",
      segs: [{ text: "No hay negativos sin responder ni temas con más quejas que en el período anterior." }],
      cta: "Ver los canales",
      target: "canales",
    });
  }

  // 2. Anuncios: consultas sin responder en piezas pagadas de Instagram y Facebook
  const paid = comments.filter(
    (c) => str(c.origin).toLowerCase() === "paid" && (channelOf(c) === "meta_ig" || channelOf(c) === "meta_fb"),
  );
  if (paid.length > 0) {
    const open = paid.filter((c) => isQuery(c) && isOpen(c));
    const byAd = new Map<string, { name: string; n: number }>();
    for (const c of open) {
      const key = str(c.post_id).trim() || str(c.post_title).trim();
      const cur = byAd.get(key);
      if (cur) cur.n += 1;
      else byAd.set(key, { name: str(c.post_title).trim(), n: 1 });
    }
    const top = Array.from(byAd.values()).sort((a, b) => b.n - a.n)[0];
    const negOpen = paid.filter((c) => toneOf(c) === "neg" && isOpen(c)).length;
    if (open.length > 0) {
      const segs: InsightSeg[] = [
        { text: `${open.length} ${plural(open.length, "consulta", "consultas")}`, bold: true },
        { text: ` en anuncios ${plural(open.length, "sigue", "siguen")} sin respuesta` },
      ];
      if (top && top.name) {
        if (open.length === 1) {
          segs.push({ text: ", en " }, { text: `«${shortTitle(top.name)}»`, bold: true });
        } else {
          segs.push({ text: ` (${top.n} en ` }, { text: `«${shortTitle(top.name)}»`, bold: true }, { text: ")" });
        }
      }
      segs.push({ text: ". Son personas interesadas que se enfrían." });
      out.push({ id: "ads", label: "Anuncios", tone: "alert", segs, cta: "Ver las publicaciones", target: "publicaciones" });
    } else if (negOpen > 0) {
      out.push({
        id: "ads",
        label: "Anuncios",
        tone: "alert",
        segs: [
          { text: `${negOpen} ${plural(negOpen, "comentario negativo", "comentarios negativos")}`, bold: true },
          { text: " en anuncios sin responder." },
        ],
        cta: "Ver las publicaciones",
        target: "publicaciones",
      });
    } else {
      out.push({
        id: "ads",
        label: "Anuncios",
        tone: "ok",
        segs: [{ text: "Los anuncios con comentarios en el período no tienen consultas ni negativos sin responder." }],
        cta: "Ver las publicaciones",
        target: "publicaciones",
      });
    }
  }

  // 3. Oportunidad: el tema más elogiado y las reseñas que se pueden usar
  const best = topics.rows
    .filter((t) => t.total >= 5 && (t.net ?? 0) > 0)
    .sort((a, b) => b.tone.pos - a.tone.pos)[0];
  if (best) {
    out.push({
      id: "opportunity",
      label: "Oportunidad",
      tone: "opportunity",
      segs: [
        { text: best.name, bold: true },
        { text: ` es lo más elogiado (+${best.net} neto, ${best.tone.pos} ${plural(best.tone.pos, "positivo", "positivos")}).` },
      ],
      cta: "Ver los temas",
      target: "temas",
    });
  }

  return out;
}

/* Sparklines para tarjetas KPI principales (calculadas con datos reales por día) */

export interface SparklineSet {
  googlePoints: string;
  volumePoints: string;
  negPoints: string;
}

export function computeDailySparklines(comments: DataRecord[]): SparklineSet {
  const byDate = new Map<string, { total: number; googleSum: number; googleCount: number; negCount: number }>();

  for (const c of comments) {
    const d = str(c.date).slice(0, 10);
    if (!d || d.length !== 10) continue;
    let entry = byDate.get(d);
    if (!entry) {
      entry = { total: 0, googleSum: 0, googleCount: 0, negCount: 0 };
      byDate.set(d, entry);
    }
    entry.total += 1;
    if (toneOf(c) === "neg") entry.negCount += 1;
    if (channelOf(c) === "gbp") {
      const r = num(c.star_rating);
      if (r !== null) {
        entry.googleSum += r;
        entry.googleCount += 1;
      }
    }
  }

  const sortedDates = Array.from(byDate.keys()).sort();
  if (sortedDates.length < 2) {
    return { googlePoints: "", volumePoints: "", negPoints: "" };
  }

  // Hasta 14 puntos distribuidos en la ventana
  const step = Math.max(1, Math.floor(sortedDates.length / 14));
  const sampledDates = sortedDates.filter((_, i) => i % step === 0 || i === sortedDates.length - 1);
  const n = sampledDates.length;

  const toPolyline = (values: (number | null)[], minBound: number, maxBound: number): string => {
    const valid = values.filter((v): v is number => v !== null);
    if (valid.length < 2) return "";
    const min = minBound;
    const max = maxBound > min ? maxBound : min + 1;
    return values
      .map((v, i) => {
        if (v === null) return null;
        const x = Math.round((i / (n - 1)) * 200);
        // Coordenadas Y en caja 0 0 200 36: entre 8 (tope superior) y 28 (piso)
        const y = Math.round(28 - ((v - min) / (max - min)) * 20);
        return `${x},${y}`;
      })
      .filter(Boolean)
      .join(" ");
  };

  const volumeVals = sampledDates.map((d) => byDate.get(d)?.total ?? 0);
  const maxVol = Math.max(...volumeVals, 1);
  const minVol = Math.min(...volumeVals);

  const googleVals = sampledDates.map((d) => {
    const e = byDate.get(d);
    return e && e.googleCount > 0 ? e.googleSum / e.googleCount : null;
  });

  const negVals = sampledDates.map((d) => {
    const e = byDate.get(d);
    return e && e.total > 0 ? (e.negCount / e.total) * 100 : 0;
  });
  const maxNeg = Math.max(...negVals, 10);

  return {
    googlePoints: toPolyline(googleVals, 1, 5),
    volumePoints: toPolyline(volumeVals, minVol, maxVol),
    negPoints: toPolyline(negVals, 0, maxNeg),
  };
}
